import {MaterialType} from '@domain/materials'
import {dataTestId, useBreakPoints} from '@hconnect/uikit'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import {TableCell, TableHead, TableRow, Typography} from '@mui/material'
import classNames from 'classnames'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {AdditivesForClinkerClassificationHeader} from './classificationHeaders/AdditivesForClinkerClassificationHeader'
import {CementClassificationHeader} from './classificationHeaders/CementClassificationHeader'
import {ClinkerClassificationHeader} from './classificationHeaders/ClinkerClassificationHeader'
import {EnhancersForCementClassificationHeader} from './classificationHeaders/EnhancersForCementClassificationHeader'
import {FuelsClassificationHeader} from './classificationHeaders/FuelsClassificationHeader'
import {IntermediateAndFinishedProductsClassificationHeader} from './classificationHeaders/IntermediateAndFinishedProductsClassificationHeader'
import {OtherGroundProductsThanCementClassificationHeader} from './classificationHeaders/OtherGroundProductsThanCementClassificationHeader'
import {OtherPyroProductsThanClinkerClassificationHeader} from './classificationHeaders/OtherPyroProductsThanClinkerClassificationHeader'
import {RawMaterialsClassificationHeader} from './classificationHeaders/RawMaterialsClassificationHeader'
import {useTableStyles} from './tableStyles'

import {
  FIRST_COLUMN_MOBILE_WIDTH,
  FIRST_COLUMN_WIDTH,
  LONG_COLUMN_WIDTH
} from '@settings/modules/common/consts'

interface MaterialsListTableHeaderProps {
  materialType: MaterialType
  showFirstColumnShadow?: boolean
  onSort: (key: string) => void
  sortKey: string
  sortDirection: 'asc' | 'desc'
}

export const MaterialsListTableHeader = ({
  materialType,
  showFirstColumnShadow,
  onSort,
  sortKey,
  sortDirection
}: MaterialsListTableHeaderProps) => {
  const {t} = useTranslation()
  const {classes} = useTableStyles()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)

  const renderSortLabel = useCallback(
    (label: string, key: string) => {
      const isSorted = sortKey === key
      const isAsc = sortDirection === 'asc'

      return (
        <Typography
          variant="caption"
          sx={{
            cursor: 'pointer',
            fontWeight: isSorted ? 'bold' : 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
          onClick={() => onSort(key)}
          {...dataTestId(`materialsSettings.table-header.${key}`)}
        >
          {label}
          {isSorted &&
            (isAsc ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />)}
        </Typography>
      )
    },
    [sortKey, sortDirection, onSort]
  )

  const renderClassificationHeader = () => {
    switch (materialType) {
      case MaterialType.Cement:
        return <CementClassificationHeader />
      case MaterialType.Clinker:
        return <ClinkerClassificationHeader />
      case MaterialType.IntermediateAndFinishedProducts:
        return <IntermediateAndFinishedProductsClassificationHeader />
      case MaterialType.RawMaterials:
        return <RawMaterialsClassificationHeader />
      case MaterialType.Fuels:
        return <FuelsClassificationHeader />
      case MaterialType.EnhancersForCement:
        return <EnhancersForCementClassificationHeader />
      case MaterialType.AdditivesForClinker:
        return <AdditivesForClinkerClassificationHeader />
      case MaterialType.OtherGroundProductsThanCement:
        return <OtherGroundProductsThanCementClassificationHeader />
      case MaterialType.OtherPyroProductsThanClinker:
        return <OtherPyroProductsThanClinkerClassificationHeader />
      default:
        return null
    }
  }

  return (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{maxWidth: isMobile ? FIRST_COLUMN_MOBILE_WIDTH : FIRST_COLUMN_WIDTH}}
          className={classNames(
            classes.stickyFirstColumnFirstRow,
            showFirstColumnShadow && classes.tableCellRightShadow
          )}
        >
          {renderSortLabel(t('materialsSettings.localName'), 'name')}
        </TableCell>
        <TableCell sx={{maxWidth: LONG_COLUMN_WIDTH}}>
          {renderSortLabel(t('materialsSettings.description'), 'description')}
        </TableCell>
        <TableCell sx={{minWidth: LONG_COLUMN_WIDTH}}>
          {renderSortLabel(t('materialsSettings.globalMaterial'), 'globalName')}
        </TableCell>
        <TableCell sx={{minWidth: LONG_COLUMN_WIDTH}}>
          {renderSortLabel(t('materialsSettings.source'), 'source')}
        </TableCell>
        {renderClassificationHeader()}
      </TableRow>
    </TableHead>
  )
}
